import React from 'react'
import PropTypes from 'prop-types'
import {
    AspectRatio,
    Box,
    Button,
    Flex,
    Img,
    ListItem,
    OrderedList,
    Text,
    useStyleConfig,
} from '@chakra-ui/react'
import {remCalc} from '../../../theme/_utils'
import useDamImage from '../../../custom-hooks/use-dam-image'
import {useGetCategoriesData} from '../../../custom-api/hooks/use-get-categories-data'
import {getGlobalLink, safeBuildUrl} from '../../../utils/contentstack-utils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {Link} from 'react-router-dom'
import SanitizedHtml from '../../html-sanitizer'
import {useProducts} from '@salesforce/commerce-sdk-react'

export const Steps = ({
    heading = '',
    image = {},
    stepsGroup = [],
    button,
    maxWidth = remCalc(1280),
}) => {
    const styles = useStyleConfig('Steps')
    const damImage = useDamImage(image)

    const {buildUrl, locale} = useMultiSite()
    const categoryData = useGetCategoriesData()
    const {data: productsData} = useProducts(
        {
            parameters: {
                ids: button?.productsfcc?.data?.length > 0 ? button.productsfcc.data[0].id : '',
                expand: ['availability', 'images'],
            },
            headers: {
                'x-debug': 'steps-index',
            },
        },
        {
            enabled: button?.productsfcc?.data?.length > 0,
        },
    )
    const {url, label, isAbsolute} = getGlobalLink({
        link: button,
        categoryData,
        productsData,
        locale: locale?.alias || locale?.id?.toLowerCase(),
    })
    const buttonProps = isAbsolute
        ? {
              as: 'a',
              href: url,
              target: '_blank',
              rel: 'noopener noreferrer',
          }
        : {
              as: Link,
              to: safeBuildUrl(url, buildUrl),
          }

    return (
        <Flex data-testid="steps" direction="column" {...styles.container} maxW={maxWidth}>
            {heading && (
                <Text as="h2" {...styles.heading}>
                    {heading}
                </Text>
            )}
            <Flex direction={{base: 'column', md: `row${damImage.url && '-reverse'}`}}>
                {damImage.url && (
                    <Box px={remCalc(24)}>
                        <AspectRatio
                            ratio={damImage.ratio}
                            width={{base: '100%', lg: remCalc(450), md: remCalc(393), sm: '100%'}}
                        >
                            <Img src={damImage.url} alt={image?.alt || ''} pb={remCalc(24)} />
                        </AspectRatio>
                    </Box>
                )}
                <Flex direction="column" alignItems={{base: 'center', md: 'flex-start'}}>
                    {stepsGroup?.length > 0 && (
                        <OrderedList {...styles.list}>
                            {stepsGroup.map((step, sIndex) => (
                                <ListItem key={`step-${sIndex}`} {...styles.listli}>
                                    <Text {...styles.listnum}>{sIndex + 1}</Text>
                                    <Box>
                                        <Text {...styles.listtitle}>{step.title}</Text>
                                        <SanitizedHtml
                                            as={Text}
                                            html={step.body_copy}
                                            {...styles.listbody}
                                        />
                                    </Box>
                                </ListItem>
                            ))}
                        </OrderedList>
                    )}
                    {label && url && (
                        <Button {...buttonProps} {...styles.cta} variant={'primary'}>
                            {label}
                        </Button>
                    )}
                </Flex>
            </Flex>
        </Flex>
    )
}

Steps.propTypes = {
    heading: PropTypes.string,
    image: PropTypes.shape(),
    stepsGroup: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            body_copy: PropTypes.string,
        }),
    ),
    button: PropTypes.shape(),
    maxWidth: PropTypes.string,
}
